import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWeightHanging, 
  faArrowLeft, 
  faSave, 
  faTimes, 
  faInfoCircle,
  faFlask,
  faTable,
  faUserCheck,
  faComment
} from '@fortawesome/free-solid-svg-icons';
import { getCustomers } from '../../../apis/customers';
import { getReviewers } from '../../../apis/reviewers';
import { createBulkDensityTestRequest } from '../../../apis/bulkDensity';

const BulkDensityMoistureForm = () => {
  const [showPreview, setShowPreview] = useState(false);
  
  // Dynamic data states
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');
  const [reviewers, setReviewers] = useState([]);
  const [loadingReviewers, setLoadingReviewers] = useState(false);

  const [formData, setFormData] = useState({
    // General Information
    customer_id: '',
    sample_description: '',
    date_of_receipt: '',
    reference_number: '',
    date_of_manufacturer: '',
    type_of_specimen: '',
    machine_used: '',
    location_of_test: 'Lab', // Constant value
    capacity_range: '',
    test_method: '',
    
    // Test Details
    sample_test_code: '',
    date_of_testing: new Date().toISOString().split('T')[0],
    date_of_report: '',
    url_number: '',
    job_code_number: '',
    manufacturer: '',
    condition_of_specimen: 'Acceptable', // Constant value
    calibration_due_date: '',
    environmental_conditions: 'Laboratory Conditions',
    
    // Test Results
    weight_before_1: '', weight_after_1: '', length_1: '', breadth_1: '', depth_1: '', volume_1: '', bulk_density_1: '', moisture_content_1: '',
    weight_before_2: '', weight_after_2: '', length_2: '', breadth_2: '', depth_2: '', volume_2: '', bulk_density_2: '', moisture_content_2: '',
    weight_before_3: '', weight_after_3: '', length_3: '', breadth_3: '', depth_3: '', volume_3: '', bulk_density_3: '', moisture_content_3: '',
    avg_bulk_density: '',
    avg_moisture_content: '',
    
    // Verification
    tested_by_name: '',
    tested_by_date: new Date().toISOString().split('T')[0],
    checked_by_name: '',
    checked_by_date: new Date().toISOString().split('T')[0],
    verified_by_name: 'Prakarsh A Sangave',
    verified_by_date: new Date().toISOString().split('T')[0],
    reviewed_by: 'Lalita S. Dussa - Quality Manager',
    authorized_by: 'Prakarsh Sangave',
    remarks: ''
  });

  // Fetch customers and reviewers on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setCustomersLoading(true);
        const customersData = await getCustomers();
        setCustomers(customersData);
        setFilteredCustomers(customersData);

        setLoadingReviewers(true);
        const reviewersData = await getReviewers();
        setReviewers(reviewersData);
      } catch (error) {
        console.error('Error fetching dynamic data:', error);
      } finally {
        setCustomersLoading(false);
        setLoadingReviewers(false);
      }
    };
    fetchData();
  }, []);

  // Filter customers based on dropdown search term
  useEffect(() => {
    if (dropdownSearchTerm.trim() === '') {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer => {
        const customerName = customer.name || customer.client_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
        return customerName.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
               (customer.phone && customer.phone.includes(dropdownSearchTerm)) ||
               (customer.city && customer.city.toLowerCase().includes(dropdownSearchTerm.toLowerCase()));
      });
      setFilteredCustomers(filtered);
    }
  }, [dropdownSearchTerm, customers]);

  // Handle customer selection from dropdown
  const handleCustomerSelect = (customer) => {
    const customerName = customer.name || customer.client_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customerName
    }));
    setCustomerSearchTerm(customerName);
    setShowCustomerDropdown(false);
    setDropdownSearchTerm(''); // Clear dropdown search
  };

  // Handle dropdown search input
  const handleDropdownSearch = (e) => {
    setDropdownSearchTerm(e.target.value);
  };

  // Handle customer input click to show dropdown
  const handleCustomerInputClick = () => {
    setShowCustomerDropdown(true);
    setDropdownSearchTerm(''); // Clear search when opening dropdown
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown]);

  // Add style CSS to override table-info background color
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .table tbody tr.table-info {
        background-color: #495057 !important;
      }
      
      .table tbody tr.table-info:hover {
        background-color: #495057 !important;
      }
      
      .table tbody tr.table-info td {
        background-color: #495057 !important;
        color: white !important;
        font-weight: 700 !important;
      }
      
      .table tbody tr.table-info:hover td {
        background-color: #495057 !important;
      }
      
      .table tbody tr.table-info td .form-control {
        background-color: #495057 !important;
        color: #ffffff !important;
        border: 1px solid #6c757d !important;
      }
      
      .transparent-table tbody tr.table-info {
        background-color: #495057 !important;
      }
      
      .transparent-table tbody tr.table-info:hover {
        background-color: #495057 !important;
      }
      
      .transparent-table tbody tr.table-info td {
        background-color: #495057 !important;
      }
      
      .transparent-table tbody tr.table-info:hover td {
        background-color: #495057 !important;
      }
      
      /* Style for Form.Select dropdowns - grey background for better visibility */
      .form-select {
        background-color: #495057 !important;
        color: white !important;
        border: 2px solid #6c757d !important;
      }
      
      .form-select:focus {
        background-color: #495057 !important;
        color: white !important;
        border-color: #FFA500 !important;
        box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
      }
      
      .form-select option {
        background-color: #495057 !important;
        color: white !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updatedData = { ...prev, [name]: value };
      
      // If it's a test result field, trigger calculations
      if (name.includes('weight') || name.includes('length') || name.includes('breadth') || name.includes('depth')) {
        return runAllCalculations(updatedData);
      }
      
      return updatedData;
    });
  };

  const runAllCalculations = (data) => {
    const newData = { ...data };
    let bdSum = 0, bdCount = 0;
    let mcSum = 0, mcCount = 0;
    
    for (let row = 1; row <= 3; row++) {
      // 1. Volume of Sample (m³) = (L * B * D) / 1,000,000,000 (converting mm to m³)
      const l = parseFloat(newData[`length_${row}`]);
      const b_dim = parseFloat(newData[`breadth_${row}`]);
      const d_dim = parseFloat(newData[`depth_${row}`]);
      
      let volume = 0;
      if (!isNaN(l) && !isNaN(b_dim) && !isNaN(d_dim)) {
        volume = (l * b_dim * d_dim) / 1000000000;
        newData[`volume_${row}`] = volume.toFixed(6);
      } else {
        newData[`volume_${row}`] = '';
      }

      // 2. Bulk Density (kg/m³) = Weight After Drying (B) / Volume (m³)
      const weightAfter = parseFloat(newData[`weight_after_${row}`]);
      if (volume > 0 && !isNaN(weightAfter)) {
        const bulkDensity = weightAfter / volume;
        newData[`bulk_density_${row}`] = bulkDensity.toFixed(1);
        bdSum += bulkDensity;
        bdCount++;
      } else {
        newData[`bulk_density_${row}`] = '';
      }

      // 3. Moisture Content (%) = ((Weight Before - Weight After) / Weight After) * 100
      const weightBefore = parseFloat(newData[`weight_before_${row}`]);
      if (!isNaN(weightBefore) && !isNaN(weightAfter) && weightAfter > 0) {
        const moistureContent = ((weightBefore - weightAfter) / weightAfter) * 100;
        newData[`moisture_content_${row}`] = moistureContent.toFixed(2);
        mcSum += moistureContent;
        mcCount++;
      } else {
        newData[`moisture_content_${row}`] = '';
      }
    }

    // 4. Update Averages
    newData.avg_bulk_density = bdCount > 0 ? (bdSum / bdCount).toFixed(1) : '';
    newData.avg_moisture_content = mcCount > 0 ? (mcSum / mcCount).toFixed(2) : '';

    return newData;
  };

  const fillRandomData = () => {
    const randomDate = (start, end) => {
      const d = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
      return d.toISOString().split('T')[0];
    };

    const descriptions = ['Coarse Aggregate 20mm', 'Fine Aggregate (M-Sand)', 'Natural Sand', 'Crushed Stone'];
    const methods = ['IS 2386 (Part 3): 1963', 'IS 2720 (Part 3): 1980', 'ASTM C127'];
    const machines = ['Sieve Shaker', 'Electronic Weighing Scale', 'Hot Air Oven'];

    // Select a random customer from dynamic list if available
    let randomCustomerId = '';
    let randomCustomerName = '';
    if (customers.length > 0) {
      const randomCust = customers[Math.floor(Math.random() * customers.length)];
      randomCustomerId = randomCust.id;
      randomCustomerName = randomCust.name || randomCust.client_name || `${randomCust.first_name || ''} ${randomCust.last_name || ''}`.trim();
    }

    // Select a random reviewer from dynamic list if available
    let randomReviewer = formData.reviewed_by;
    if (reviewers.length > 0) {
      const randomRev = reviewers[Math.floor(Math.random() * reviewers.length)];
      randomReviewer = `${randomRev.name} - ${randomRev.designation}`;
    }

    const newRandomData = {
      ...formData,
      customer_id: randomCustomerId,
      customer_name: randomCustomerName,
      reviewed_by: randomReviewer,
      sample_description: descriptions[Math.floor(Math.random() * descriptions.length)],
      sample_test_code: `BD-${Math.floor(1000 + Math.random() * 9000)}`,
      reference_number: `REF-${Math.floor(10000 + Math.random() * 90000)}`,
      job_code_number: `JOB-${Math.floor(1000 + Math.random() * 9000)}`,
      url_number: `ULR-${Math.floor(100000 + Math.random() * 900000)}`,
      date_of_receipt: randomDate(new Date(2023, 0, 1), new Date()),
      date_of_testing: new Date().toISOString().split('T')[0],
      date_of_report: new Date().toISOString().split('T')[0],
      manufacturer: 'Local Supplier',
      date_of_manufacturer: randomDate(new Date(2022, 0, 1), new Date(2023, 0, 1)),
      type_of_specimen: 'Aggregate',
      condition_of_specimen: 'Acceptable', // Constant value
      machine_used: machines[Math.floor(Math.random() * machines.length)],
      location_of_test: 'Lab', // Constant value
      capacity_range: '0-50 kg',
      test_method: methods[Math.floor(Math.random() * methods.length)],
      calibration_due_date: randomDate(new Date(), new Date(2025, 11, 31)),
      environmental_conditions: 'Temp: 27±2°C, Humidity: 65%',
      tested_by_name: 'John Doe',
      checked_by_name: 'Jane Smith',
      remarks: 'Sample tested as per standard procedures. No deviations observed.'
    };

    // Fill test result rows with logical values
    for (let i = 1; i <= 3; i++) {
      const weightAfter = parseFloat((5 + Math.random() * 5).toFixed(3)); // 5-10 kg
      const moisture = Math.random() * 5; // 0-5% moisture
      const weightBefore = parseFloat((weightAfter * (1 + moisture/100)).toFixed(3));
      
      newRandomData[`weight_before_${i}`] = weightBefore.toString();
      newRandomData[`weight_after_${i}`] = weightAfter.toString();
      newRandomData[`length_${i}`] = '150';
      newRandomData[`breadth_${i}`] = '150';
      newRandomData[`depth_${i}`] = '150';
    }

    setCustomerSearchTerm(randomCustomerName);
    setFormData(runAllCalculations(newRandomData));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowPreview(true);
  };

  const handleConfirmSubmit = async (e, type = 'report') => {
    console.log('Form submitted:', formData, 'Type:', type);
    
    // Validate required fields
    if (!formData.customer_id) {
      alert('Please select a customer');
      return;
    }
    
    try {
      // Prepare data for API
      const testRequestData = {
        customer_id: formData.customer_id,
        url_number: formData.url_number,
        ulr_number: formData.url_number,  // Alias
        job_code_number: formData.job_code_number,
        job_number: formData.job_code_number,  // Alias
        reference_number: formData.reference_number,
        sample_description: formData.sample_description,
        material_description: formData.sample_description,  // Alias
        date_of_receipt: formData.date_of_receipt,
        test_method: formData.test_method || 'IS 2386 (Part III): 1963',
        sample_test_code: formData.sample_test_code,
        date_of_testing: formData.date_of_testing,
        environmental_conditions: formData.environmental_conditions || 'Laboratory Conditions',
        tested_by_name: formData.tested_by_name,
        tested_by_date: formData.tested_by_date,
        checked_by_name: formData.checked_by_name,
        checked_by_date: formData.checked_by_date,
        verified_by_name: formData.verified_by_name,
        verified_by_date: formData.verified_by_date,
        reviewed_by: formData.reviewed_by,
        authorized_by: formData.authorized_by,
        remarks: formData.remarks,
        avg_bulk_density: formData.avg_bulk_density,
        avg_moisture_content: formData.avg_moisture_content,
        status: 'completed'
      };
      
      // Add readings data
      for (let i = 1; i <= 3; i++) {
        if (formData[`weight_before_${i}`] || formData[`weight_after_${i}`]) {
          testRequestData[`weight_before_${i}`] = formData[`weight_before_${i}`];
          testRequestData[`weight_after_${i}`] = formData[`weight_after_${i}`];
          testRequestData[`length_${i}`] = formData[`length_${i}`];
          testRequestData[`breadth_${i}`] = formData[`breadth_${i}`];
          testRequestData[`depth_${i}`] = formData[`depth_${i}`];
          testRequestData[`volume_${i}`] = formData[`volume_${i}`];
          testRequestData[`bulk_density_${i}`] = formData[`bulk_density_${i}`];
          testRequestData[`moisture_content_${i}`] = formData[`moisture_content_${i}`];
        }
      }
      
      // Save to database
      console.log('💾 Saving Bulk Density test to database...');
      const response = await createBulkDensityTestRequest(testRequestData);
      console.log('✅ Bulk Density test saved successfully:', response);
    
    // Helper to format date from YYYY-MM-DD to YYYY/MM/DD
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      return dateStr.replace(/-/g, '/');
    };
    
    // Create URL parameters from form data
    const params = new URLSearchParams();
    
    // General Information
    params.append('customer_name', formData.customer_name || formData.customer_id || '');
    params.append('reference_number', formData.reference_number || '');
    params.append('capacity_range', formData.capacity_range || '');
    params.append('test_method', formData.test_method || '');
    
    // Test Results with unit conversion for Report (kg to gm, mm to cm, m3 to cm3, kg/m3 to gm/cm3)
    for (let i = 1; i <= 3; i++) {
      const weightBefore = parseFloat(formData[`weight_before_${i}`]) || 0;
      const weightAfter = parseFloat(formData[`weight_after_${i}`]) || 0;
      const length = parseFloat(formData[`length_${i}`]) || 0;
      const breadth = parseFloat(formData[`breadth_${i}`]) || 0;
      const depth = parseFloat(formData[`depth_${i}`]) || 0;
      const volume = parseFloat(formData[`volume_${i}`]) || 0;
      const bulkDensity = parseFloat(formData[`bulk_density_${i}`]) || 0;

      if (type === 'report') {
        params.append(`weight_before_${i}`, (weightBefore * 1000).toFixed(0)); // kg to gm
        params.append(`weight_after_${i}`, (weightAfter * 1000).toFixed(0)); // kg to gm
        params.append(`length_${i}`, (length / 10).toFixed(1)); // mm to cm
        params.append(`breadth_${i}`, (breadth / 10).toFixed(1)); // mm to cm
        params.append(`depth_${i}`, (depth / 10).toFixed(1)); // mm to cm
        params.append(`volume_${i}`, (volume * 1000000).toFixed(0)); // m3 to cm3
        params.append(`bulk_density_${i}`, (bulkDensity / 1000).toFixed(3)); // kg/m3 to gm/cm3
      } else {
        params.append(`weight_before_${i}`, weightBefore.toFixed(3));
        params.append(`weight_after_${i}`, weightAfter.toFixed(3));
        params.append(`length_${i}`, length.toFixed(1));
        params.append(`breadth_${i}`, breadth.toFixed(1));
        params.append(`depth_${i}`, depth.toFixed(1));
        params.append(`volume_${i}`, volume.toFixed(6));
        params.append(`bulk_density_${i}`, bulkDensity.toFixed(1));
      }
      params.append(`moisture_content_${i}`, formData[`moisture_content_${i}`] || '0.00');
    }

    const avgBulkDensity = parseFloat(formData.avg_bulk_density) || 0;
    if (type === 'report') {
      params.append('avg_bulk_density', (avgBulkDensity / 1000).toFixed(3));
    } else {
      params.append('avg_bulk_density', avgBulkDensity.toFixed(1));
    }
    params.append('avg_moisture_content', formData.avg_moisture_content || '0.00');
    
    // Customer Information
    params.append('ulr_number', formData.url_number || '');
    params.append('job_code_number', formData.job_code_number || '');
    params.append('reference_number', formData.reference_number || '');
    params.append('date_of_report', formatDate(formData.date_of_report) || '');
    params.append('date_of_material_receipt', formatDate(formData.date_of_receipt) || '');
    params.append('date_of_receipt', formatDate(formData.date_of_receipt) || '');
    params.append('date_of_testing', formatDate(formData.date_of_testing) || '');
    params.append('material_description', formData.sample_description || '');
    params.append('sample_description', formData.sample_description || '');
    params.append('condition_of_sample', formData.condition_of_specimen || '');
    params.append('location_of_testing', formData.location_of_test || '');
    params.append('manufacturer', formData.manufacturer || '');
    params.append('date_of_manufacturer', formatDate(formData.date_of_manufacturer) || '');
    params.append('type_of_specimen', formData.type_of_specimen || '');
    params.append('machine_used', formData.machine_used || '');
    params.append('capacity_range', formData.capacity_range || '');
    params.append('calibration_due_date', formatDate(formData.calibration_due_date) || '');
    params.append('environmental_conditions', formData.environmental_conditions || '');
    params.append('test_witnessed', formData.test_witnessed || '');
    
    // For Observation Sheet signatures
    params.append('tested_by_date', formatDate(formData.tested_by_date) || '');
    params.append('checked_by_date', formatDate(formData.checked_by_date) || '');
    params.append('verified_by_date', formatDate(formData.verified_by_date) || '');
    params.append('tested_by_name', formData.tested_by_name || '');
    params.append('checked_by_name', formData.checked_by_name || '');
    params.append('verified_by_name', formData.verified_by_name || '');
    params.append('sample_test_code', formData.sample_test_code || '');
    params.append('remarks', formData.remarks || '');
    
    // Authorization
    params.append('reviewed_by', formData.reviewed_by || 'Lalita S. Dussa - Quality Manager');
    params.append('authorized_by', formData.authorized_by || 'Prakarsh Sangave');
    
    // Build the report URL
    const reportUrl = type === 'report' 
      ? `/BulkDensity/BulkDensityReport.html?${params.toString()}`
      : `/BulkDensity/BulkDensityObservationSheet.html?${params.toString()}`;
    
    // Open the report in a new tab
    window.open(reportUrl, '_blank');
    
    setShowPreview(false);
    } catch (error) {
      console.error('❌ Error saving Bulk Density test:', error);
      alert('Failed to save Bulk Density test. Please try again.\n\nError: ' + (error.message || 'Unknown error'));
    }
  };

  const handleEditForm = () => {
    setShowPreview(false);
  };

  // Preview Form Component
  const PreviewForm = () => (
    <div>
            <div style={{ backgroundColor: '#28a745', padding: '2rem 0', borderRadius: '15px' }}>
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faWeightHanging} className="me-3" style={{ fontSize: '2rem', color: 'white' }} />
                <div>
                  <h1 style={{ color: 'white', fontWeight: '700', margin: 0 }}>
                    Bulk Density & Moisture Content - Preview
                  </h1>
                  <p className="mb-0 mt-2" style={{ color: 'white' }}>
                    Review all details before final submission
                  </p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button onClick={handleEditForm} variant="light" className="btn-vitrag-primary me-2">
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Edit Form
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        {/* General Information Preview */}
        <Row className="mb-4">
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  General Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Customer:</strong> {formData.customer_name || formData.customer_id}</p>
                    <p><strong>Sample Description:</strong> {formData.sample_description}</p>
                    <p><strong>Sample Test Code:</strong> {formData.sample_test_code}</p>
                    <p><strong>Reference Number:</strong> {formData.reference_number}</p>
                    <p><strong>Job Code Number:</strong> {formData.job_code_number}</p>
                    <p><strong>URL Number:</strong> {formData.url_number}</p>
                    <p><strong>Date of Receipt:</strong> {formData.date_of_receipt}</p>
                    <p><strong>Date of Testing:</strong> {formData.date_of_testing}</p>
                    <p><strong>Date of Report:</strong> {formData.date_of_report}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Manufacturer:</strong> {formData.manufacturer}</p>
                    <p><strong>Date of Manufacturer:</strong> {formData.date_of_manufacturer}</p>
                    <p><strong>Type of Specimen:</strong> {formData.type_of_specimen}</p>
                    <p><strong>Condition of Specimen:</strong> {formData.condition_of_specimen}</p>
                    <p><strong>Machine Used:</strong> {formData.machine_used}</p>
                    <p><strong>Location of Test:</strong> {formData.location_of_test}</p>
                    <p><strong>Capacity Range:</strong> {formData.capacity_range}</p>
                    <p><strong>Test Method:</strong> {formData.test_method}</p>
                    <p><strong>Calibration Due Date:</strong> {formData.calibration_due_date}</p>
                    <p><strong>Environmental Conditions:</strong> {formData.environmental_conditions}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Test Results Preview */}
        <Row className="mb-4">
          <Col>
            <Card className="card-vitrag shadow-sm">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                  <FontAwesomeIcon icon={faTable} className="me-2" />
                  Test Results
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm" style={{ fontSize: '0.875rem' }}>
                    <thead className="table-dark">
                      <tr>
                        <th>Sr. No.</th>
                        <th>Weight Before (kg)</th>
                        <th>Weight After (kg)</th>
                        <th>Length (mm)</th>
                        <th>Breadth (mm)</th>
                        <th>Depth (mm)</th>
                        <th>Volume (m³)</th>
                        <th>Bulk Density (kg/m³)</th>
                        <th>Moisture Content (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3].map((row) => (
                        <tr key={row}>
                          <td>{row}</td>
                          <td>{formData[`weight_before_${row}`]}</td>
                          <td>{formData[`weight_after_${row}`]}</td>
                          <td>{formData[`length_${row}`]}</td>
                          <td>{formData[`breadth_${row}`]}</td>
                          <td>{formData[`depth_${row}`]}</td>
                          <td>{formData[`volume_${row}`]}</td>
                          <td>{formData[`bulk_density_${row}`]}</td>
                          <td>{formData[`moisture_content_${row}`]}</td>
                        </tr>
                      ))}
                      <tr className="table-secondary">
                        <td colSpan="7" className="text-end fw-bold">Average</td>
                        <td className="text-center fw-bold">{formData.avg_bulk_density}</td>
                        <td className="text-center fw-bold">{formData.avg_moisture_content}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Row>
          <Col className="text-center">
            <Button onClick={handleEditForm} variant="secondary" size="lg" className="me-3">
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Edit Form
            </Button>
            <Button onClick={(e) => handleConfirmSubmit(e, 'report')} variant="success" size="lg" className="me-3">
              <FontAwesomeIcon icon={faSave} className="me-2" />
              Generate Report
            </Button>
            <Button onClick={(e) => handleConfirmSubmit(e, 'observation')} variant="info" size="lg" className="me-3">
              <FontAwesomeIcon icon={faTable} className="me-2" />
              Generate Observation Sheet
            </Button>
            <Button as={Link} to="/other-services" variant="danger" size="lg" className="px-5">
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              Cancel
            </Button>
          </Col>
        </Row>
      </Container>
    </div>
  );

  if (showPreview) {
    return <PreviewForm />;
  }

  return (
    <div>
            <div style={{ backgroundColor: '#28a745', padding: '2rem 0', borderRadius: '15px' }}>
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faWeightHanging} className="me-3" style={{ fontSize: '2rem', color: 'white' }} />
                <div>
                  <h1 style={{ color: 'white', fontWeight: '700', margin: 0 }}>
                    Bulk Density and Moisture Content Testing
                  </h1>
                  <p className="mb-0 mt-2" style={{ color: 'white' }}>
                    Material density analysis and moisture content testing
                  </p>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button as={Link} to="/other-services" variant="light" className="btn-vitrag-primary">
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Other Services
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        <Form onSubmit={handleSubmit}>
          {/* General Information */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    General Information
                  </h5>
                </Card.Header>
              <Card.Body className="transparent-input-section">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3 position-relative customer-dropdown-container">
                      <Form.Label>Customer *</Form.Label>
                      {customersLoading ? (
                        <div className="d-flex align-items-center">
                          <Spinner animation="border" size="sm" className="me-2" />
                          <span style={{ color: 'white' }}>Loading customers...</span>
                        </div>
                      ) : (
                        <div className="customer-dropdown-container" style={{ position: 'relative' }}>
                      <Form.Control
                        type="text"
                        value={customerSearchTerm}
                            onChange={(e) => setCustomerSearchTerm(e.target.value)}
                            onClick={handleCustomerInputClick}
                            placeholder="Search and select customer..."
                        required
                            autoComplete="off"
                            readOnly
                            style={{ 
                              cursor: 'pointer',
                              backgroundColor: '#1C2333',
                              border: '2px solid #6c757d',
                              borderRadius: '8px',
                              color: 'white',
                              padding: '12px 15px',
                              fontSize: '1rem'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#FFA500';
                              e.target.style.boxShadow = '0 0 0 0.25rem rgba(255, 165, 0, 0.25)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#6c757d';
                              e.target.style.boxShadow = 'none';
                            }}
                      />
                      {showCustomerDropdown && (
                            <div
                              style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: '#1C2333',
                                border: '2px solid #6c757d',
                                borderTop: 'none',
                                borderRadius: '0 0 8px 8px',
                                maxHeight: '300px',
                          overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
                              }}
                            >
                              {/* Search input inside dropdown */}
                              <div style={{ padding: '15px', borderBottom: '1px solid #6c757d' }}>
                                <Form.Control
                                  type="text"
                                  placeholder="Search customers..."
                                  value={dropdownSearchTerm}
                                  onChange={handleDropdownSearch}
                                  autoComplete="off"
                                  style={{
                                    backgroundColor: '#242B3C',
                                    border: '2px solid #6c757d',
                                    borderRadius: '8px',
                                    color: 'white',
                                    padding: '10px 12px',
                                    fontSize: '0.9rem'
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#FFA500';
                                    e.target.style.boxShadow = '0 0 0 0.2rem rgba(255, 165, 0, 0.25)';
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#6c757d';
                                    e.target.style.boxShadow = 'none';
                                  }}
                                />
                              </div>
                              
                              {/* Customer list */}
                          {filteredCustomers.length > 0 ? (
                            filteredCustomers.map((customer) => {
                              const customerName = customer.name || customer.client_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                              return (
                                <div
                                  key={customer.id}
                                  onClick={() => handleCustomerSelect(customer)}
                                  style={{
                                        padding: '10px 15px',
                                    cursor: 'pointer',
                                        borderBottom: '1px solid #6c757d',
                                    color: 'white',
                                        backgroundColor: 'transparent'
                                  }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a3441'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                      <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                  {customerName}
                                      </div>
                                      <div style={{ fontSize: '0.85em', color: '#adb5bd' }}>
                                        {customer.phone && `${customer.phone} • `}
                                        {customer.city && customer.city}
                                      </div>
                                </div>
                              );
                            })
                          ) : (
                                <div
                                  style={{
                                    padding: '15px',
                                    color: '#adb5bd',
                                    textAlign: 'center'
                                  }}
                                >
                                  {dropdownSearchTerm.trim() !== '' 
                                    ? `No customers found matching "${dropdownSearchTerm}"`
                                    : 'No customers available'
                                  }
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Sample Description *</Form.Label>
                        <Form.Control as="textarea" rows={2} name="sample_description" value={formData.sample_description} onChange={handleInputChange} placeholder="Describe the sample..." required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Sample Test Code Number *</Form.Label>
                        <Form.Control type="text" name="sample_test_code" value={formData.sample_test_code} onChange={handleInputChange} placeholder="e.g., SCN-2024001" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Reference Number</Form.Label>
                        <Form.Control type="text" name="reference_number" value={formData.reference_number} onChange={handleInputChange} placeholder="Enter reference number" />
                      </Form.Group>
                    
                      <Form.Group className="mb-3">
                        <Form.Label>Job Code Number *</Form.Label>
                        <Form.Control type="text" name="job_code_number" value={formData.job_code_number} onChange={handleInputChange} placeholder="Enter job code number" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>URL Number *</Form.Label>
                        <Form.Control type="text" name="url_number" value={formData.url_number} onChange={handleInputChange} placeholder="Enter URL number" required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Receipt *</Form.Label>
                        <Form.Control type="date" name="date_of_receipt" value={formData.date_of_receipt} onChange={handleInputChange} required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Testing *</Form.Label>
                        <Form.Control type="date" name="date_of_testing" value={formData.date_of_testing} onChange={handleInputChange} required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Report</Form.Label>
                        <Form.Control type="date" name="date_of_report" value={formData.date_of_report} onChange={handleInputChange} />
                      </Form.Group>
                    </Col>
                      
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Manufacturer</Form.Label>
                        <Form.Control type="text" name="manufacturer" value={formData.manufacturer} onChange={handleInputChange} placeholder="Enter manufacturer name" />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Date of Manufacturer</Form.Label>
                        <Form.Control type="date" name="date_of_manufacturer" value={formData.date_of_manufacturer} onChange={handleInputChange} />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Type of Specimen</Form.Label>
                        <Form.Control type="text" name="type_of_specimen" value={formData.type_of_specimen} onChange={handleInputChange} placeholder="e.g., Concrete, Aggregate, etc." />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Condition of Specimen</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="condition_of_specimen" 
                          value={formData.condition_of_specimen || 'Acceptable'} 
                          readOnly
                          style={{ backgroundColor: '#2a3441', color: '#adb5bd', cursor: 'not-allowed' }}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Machine used for Testing</Form.Label>
                        <Form.Control type="text" name="machine_used" value={formData.machine_used} onChange={handleInputChange} placeholder="e.g., Compression Testing Machine" />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Location of Test</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="location_of_test" 
                          value={formData.location_of_test || 'Lab'} 
                          readOnly
                          style={{ backgroundColor: '#2a3441', color: '#adb5bd', cursor: 'not-allowed' }}
                        />
                      </Form.Group>
                    
                      <Form.Group className="mb-3">
                        <Form.Label>Capacity/Range</Form.Label>
                        <Form.Control type="text" name="capacity_range" value={formData.capacity_range} onChange={handleInputChange} placeholder="e.g., 2000 kN, 0-100%" />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Test Method *</Form.Label>
                        <Form.Control type="text" name="test_method" value={formData.test_method} onChange={handleInputChange} required />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Calibration Due Date</Form.Label>
                        <Form.Control type="date" name="calibration_due_date" value={formData.calibration_due_date} onChange={handleInputChange} />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Environmental Conditions</Form.Label>
                        <Form.Control type="text" name="environmental_conditions" value={formData.environmental_conditions} onChange={handleInputChange} placeholder="e.g., Laboratory Conditions" />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Test Results Table */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faTable} className="me-2" />
                    Test Results
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%', border: '2px solid #6c757d', borderRadius: '8px', backgroundColor: '#1C2333' }}>
                      <thead className="table-dark">
                        <tr>
                          <th rowSpan="2" style={{ width: '50px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Sr. No.</th>
                          <th rowSpan="2" style={{ width: '140px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Weight of Sample before oven drying (kg)</th>
                          <th rowSpan="2" style={{ width: '140px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Weight of Sample after oven drying (kg)</th>
                          <th colSpan="3" style={{ padding: '10px', border: '1px solid #6c757d', textAlign: 'center', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Size of Sample</th>
                          <th rowSpan="2" style={{ width: '100px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Volume of Sample (m³)</th>
                          <th rowSpan="2" style={{ width: '120px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Bulk Density (kg/m³)</th>
                          <th rowSpan="2" style={{ width: '120px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Moisture Content (%)</th>
                        </tr>
                        <tr>
                          <th style={{ width: '100px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.9rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>L (mm)</th>
                          <th style={{ width: '100px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.9rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>B (mm)</th>
                          <th style={{ width: '100px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.9rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>D (mm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1, 2, 3].map((row) => (
                          <tr key={row} style={{ backgroundColor: '#1C2333' }}>
                            <td style={{ padding: '8px', border: '1px solid #6c757d', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#1C2333', color: 'white' }}>{row}</td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control size="sm" type="number" step="0.001" name={`weight_before_${row}`} value={formData[`weight_before_${row}`]} onChange={handleInputChange} style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }} />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control size="sm" type="number" step="0.001" name={`weight_after_${row}`} value={formData[`weight_after_${row}`]} onChange={handleInputChange} style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }} />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control size="sm" type="number" step="0.1" name={`length_${row}`} value={formData[`length_${row}`]} onChange={handleInputChange} style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }} />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control size="sm" type="number" step="0.1" name={`breadth_${row}`} value={formData[`breadth_${row}`]} onChange={handleInputChange} style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }} />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control size="sm" type="number" step="0.1" name={`depth_${row}`} value={formData[`depth_${row}`]} onChange={handleInputChange} style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }} />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control size="sm" type="number" step="0.000001" name={`volume_${row}`} value={formData[`volume_${row}`]} readOnly style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }} />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control size="sm" type="number" step="0.1" name={`bulk_density_${row}`} value={formData[`bulk_density_${row}`]} readOnly style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }} />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control size="sm" type="number" step="0.01" name={`moisture_content_${row}`} value={formData[`moisture_content_${row}`]} readOnly style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }} />
                            </td>
                          </tr>
                        ))}
                        <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                          <td colSpan="7" className="text-center fw-bold" style={{ padding: '10px', border: '1px solid #6c757d', fontSize: '1rem', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>Average</td>
                          <td className="text-center fw-bold" style={{ padding: '8px', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                            <Form.Control 
                              size="sm" 
                              type="number" 
                              step="0.1" 
                              name="avg_bulk_density" 
                              value={formData.avg_bulk_density} 
                              readOnly 
                              className="average-input-transparent"
                              style={{ 
                                fontSize: '0.9rem', 
                                textAlign: 'center',
                                backgroundColor: '#495057',
                                border: '1px solid #6c757d',
                                borderRadius: '6px',
                                color: '#ffffff',
                                fontWeight: '700',
                                cursor: 'not-allowed'
                              }} 
                            />
                          </td>
                          <td className="text-center fw-bold" style={{ padding: '8px', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                            <Form.Control 
                              size="sm" 
                              type="number" 
                              step="0.01" 
                              name="avg_moisture_content" 
                              value={formData.avg_moisture_content} 
                              readOnly 
                              className="average-input-transparent"
                              style={{ 
                                fontSize: '0.9rem', 
                                textAlign: 'center',
                                backgroundColor: '#495057',
                                border: '1px solid #6c757d',
                                borderRadius: '6px',
                                color: '#ffffff',
                                fontWeight: '700',
                                cursor: 'not-allowed'
                              }} 
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Verification */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Verification
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Row>
                    <Col md={4}>
                      <h6>Tested By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Name *</Form.Label>
                        <Form.Control type="text" name="tested_by_name" value={formData.tested_by_name} onChange={handleInputChange} placeholder="Enter tester name" required />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control type="date" name="tested_by_date" value={formData.tested_by_date} onChange={handleInputChange} required />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6>Checked By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Name *</Form.Label>
                        <Form.Control type="text" name="checked_by_name" value={formData.checked_by_name} onChange={handleInputChange} placeholder="Enter checker name" required />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control type="date" name="checked_by_date" value={formData.checked_by_date} onChange={handleInputChange} required />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6>Verified By</h6>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control type="text" name="verified_by_name" value={formData.verified_by_name} readOnly style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }} />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Date *</Form.Label>
                        <Form.Control type="date" name="verified_by_date" value={formData.verified_by_date} onChange={handleInputChange} required />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Remarks */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faComment} className="me-2" />
                    Remarks
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Control as="textarea" rows={4} name="remarks" value={formData.remarks} onChange={handleInputChange} placeholder="Enter any additional remarks or observations..." />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Review & Authorization */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Reviewed By
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Label>Select Reviewer:</Form.Label>
                    <Form.Select 
                      name="reviewed_by" 
                      value={formData.reviewed_by} 
                      onChange={handleInputChange} 
                      required
                      style={{
                        backgroundColor: '#495057',
                        border: '2px solid #6c757d',
                        borderRadius: '8px',
                        color: 'white',
                        padding: '12px 15px',
                        fontSize: '1rem'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#FFA500';
                        e.target.style.boxShadow = '0 0 0 0.25rem rgba(255, 165, 0, 0.25)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#6c757d';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="" style={{ backgroundColor: '#495057', color: 'white' }}>-- Select a reviewer --</option>
                      {reviewers.map((reviewer) => {
                        const reviewerValue = `${reviewer.name} - ${reviewer.designation}`;
                        return (
                          <option key={reviewer.id} value={reviewerValue} style={{ backgroundColor: '#495057', color: 'white' }}>
                            {reviewerValue}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Authorized By
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Label>Authorized By:</Form.Label>
                    <Form.Control type="text" name="authorized_by" value={formData.authorized_by} readOnly style={{ backgroundColor: 'rgba(108, 117, 125, 0.1)', borderColor: '#495057', opacity: 0.9 }} />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row>
            <Col className="text-center">
              <Button type="submit" variant="primary" size="lg" className="me-3">
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Save Test Data
              </Button>
              <Button as={Link} to="/other-services" variant="secondary" size="lg" className="px-5">
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default BulkDensityMoistureForm;
